import { useList, useUpdate, useDelete, useInvalidate, useCustomMutation } from '@refinedev/core';
import { useState, useMemo, useEffect } from 'react';
import { Edit, Eye, Save, X, Loader2, Send, Copy, Check, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormHeader } from '@/components/forms';
import { DataTable, TableToolbar, type Column, type BulkAction, type SortState } from '@/components/tables';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { notify } from '@/lib/notify';
import { translateErrorMessage } from '@/providers/notification-provider';
import { resolveApiUrl } from '@/providers/api-url';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import type { ApiResponse } from '@/types';

interface IntegrationService {
  id: string;
  category: string;
  label: string;
  logo: string;
  key: string;
  value: Record<string, string>;
  isConfigured: boolean;
}

interface FiscalBusinessRuConfig {
  accountUrl: string;
  appId: string;
  secret: string;
  cashier: string;
  taxationSystem: string;
  vatRate: string;
  callbackUrl: string;
  /**
   * Реально-передаваемый callback_url в команды Бизнес.ру: либо ручной
   * callbackUrl, либо авто `${backendBaseUrl}/api/fiscal/business-ru/callback`.
   * Только для отображения в UI (read-only) — пользователь копирует и вбивает
   * в ЛК Бизнес.ру.
   */
  effectiveCallbackUrl: string;
  configured: boolean;
}

interface FiscalConfigResponse {
  provider: 'CLOUDPAYMENTS' | 'BUSINESS_RU';
  cnumPrefix: string;
  businessRu: FiscalBusinessRuConfig;
}

interface FiscalHealthcheckResponse {
  ok?: boolean;
  error?: string;
}

type SelectOption = { value: string; label: string };

type FieldDef = {
  key: string;
  label: string;
  type: 'text' | 'password' | 'toggle' | 'select';
  required?: boolean;
  options?: SelectOption[];
  placeholder?: string;
  colSpan?: 'full' | 'half';
  /**
   * Поле рендерится только когда editValue[dependsOn.key] === dependsOn.value.
   * Используется для условных секций (например, fiscal-провайдер: при
   * fiscalProvider=CLOUDPAYMENTS показываем поля CP-чеков, при BUSINESS_RU —
   * поля Бизнес.ру).
   */
  dependsOn?: { key: string; value: string };
};

type SectionDef = {
  section: string;
  columns?: 1 | 2;
  fields: FieldDef[];
};

type ServiceConfig = FieldDef[] | SectionDef[];

function isSectioned(config: ServiceConfig): config is SectionDef[] {
  // config может отсутствовать, если бэкенд отдал сервис, описания полей для
  // которого здесь ещё нет: раньше это роняло всю страницу интеграций.
  return Array.isArray(config) && config.length > 0 && 'section' in config[0];
}

const SERVICE_FIELDS: Record<string, ServiceConfig> = {
  amocrm: [
    { key: 'clientId', label: 'Client ID', type: 'text', required: true },
    { key: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
    { key: 'redirectUri', label: 'Redirect URI', type: 'text' },
    { key: 'subdomain', label: 'Subdomain', type: 'text' },
  ],
  openrouter: [{ key: 'apiKey', label: 'API Key', type: 'password', required: true }],
  mistral: [
    { key: 'apiKey', label: 'API Key', type: 'password', required: true },
    {
      key: 'defaultModel',
      label: 'Модель по умолчанию',
      type: 'select',
      options: [
        { value: 'mistral-small-latest', label: 'Small — самая быстрая, для эфира' },
        { value: 'mistral-medium-latest', label: 'Medium — баланс скорости и качества' },
        { value: 'mistral-large-latest', label: 'Large — лучшее качество, медленнее' },
        { value: 'ministral-8b-latest', label: 'Ministral 8B — облегчённая, быстрее Small' },
        { value: 'ministral-3b-latest', label: 'Ministral 3B — самая лёгкая, заметно проще качеством' },
      ],
    },
  ],
  elevenlabs: [
    { key: 'apiKey', label: 'API Key', type: 'password', required: true },
    {
      key: 'defaultSttModelId',
      label: 'Модель распознавания',
      type: 'select',
      // Список сверен с ответом провайдера живым запросом: других моделей
      // распознавания у ElevenLabs нет. Пункт `scribe_v2_realtime` убран —
      // такой модели не существует, на запрос с ней приходит
      // «is not a valid model_id», а бэкенд молча подменял её на scribe_v2.
      options: [
        { value: 'scribe_v2', label: 'Scribe v2 — текущее поколение' },
        { value: 'scribe_v1', label: 'Scribe v1 — предыдущее поколение' },
        { value: 'scribe_v1_experimental', label: 'Scribe v1 experimental' },
      ],
    },
    {
      key: 'defaultTtsModelId',
      label: 'Модель озвучки',
      type: 'select',
      // Задержка в подписях — замер на проде (одна фраза, по три прогона на
      // модель). Она целиком прибавляется к отставанию озвучки от говорящего.
      options: [
        { value: 'eleven_flash_v2_5', label: 'Flash v2.5 — ~0.4 с, самая быстрая' },
        { value: 'eleven_turbo_v2_5', label: 'Turbo v2.5 — ~0.4 с' },
        { value: 'eleven_flash_v2', label: 'Flash v2 — быстрая' },
        { value: 'eleven_turbo_v2', label: 'Turbo v2 — баланс' },
        { value: 'eleven_multilingual_v2', label: 'Multilingual v2 — ~1.2 с, качество' },
        { value: 'eleven_v3', label: 'Eleven v3 — ~2.6 с, выразительная, но медленная' },
      ],
    },
    {
      key: 'defaultTtsHqModelId',
      label: 'Модель озвучки для записей',
      type: 'select',
      // Для записей задержка не важна — берём модели поинтереснее.
      options: [
        { value: 'eleven_v3', label: 'Eleven v3 — выразительная' },
        { value: 'eleven_multilingual_v2', label: 'Multilingual v2 — качество' },
        { value: 'eleven_turbo_v2_5', label: 'Turbo v2.5 — баланс' },
      ],
    },
    // Перевод речи эфира логически часть этой же интеграции (транскрипция
    // ElevenLabs → перевод → озвучка), поэтому провайдер и модель перевода
    // настраиваются здесь же, а не в отдельной интеграции.
    {
      key: 'mtProvider',
      label: 'Провайдер перевода',
      type: 'select',
      options: [
        { value: 'mistral', label: 'Mistral — прямой, быстрее' },
        { value: 'openrouter', label: 'OpenRouter — агрегатор моделей' },
      ],
    },
    {
      key: 'mtModelId',
      label: 'Модель перевода (пусто — по умолчанию)',
      type: 'text',
      placeholder: 'Например, mistral-small-latest',
    },
    // Адрес API. Меняется на собственный ретранслятор там, где провайдер
    // отклоняет прямые серверные запросы по региону.
    { key: 'baseUrl', label: 'Адрес API', type: 'text' },
    { key: 'relaySecret', label: 'Секрет ретранслятора', type: 'password' },
  ],
  click: [
    { key: 'merchantId', label: 'Merchant ID', type: 'text', required: true },
    { key: 'serviceId', label: 'Service ID', type: 'text', required: true },
    { key: 'secretKey', label: 'Secret Key', type: 'password', required: true },
  ],
  payme: [
    { key: 'merchantId', label: 'Merchant ID', type: 'text', required: true },
    { key: 'secretKey', label: 'Secret Key', type: 'password', required: true },
  ],
  stripe: [
    { key: 'secretKey', label: 'Secret Key', type: 'password', required: true },
    { key: 'webhookSecret', label: 'Webhook Secret', type: 'password' },
  ],
  telegram_client: [
    { key: 'apiId', label: 'API ID', type: 'text', required: true },
    { key: 'apiHash', label: 'API Hash', type: 'password', required: true },
  ],
  email_smtp: [
    { key: 'host', label: 'SMTP Host', type: 'text', required: true },
    { key: 'port', label: 'Port', type: 'text', required: true },
    { key: 'user', label: 'Username', type: 'text', required: true },
    { key: 'password', label: 'Password', type: 'password', required: true },
    { key: 'fromEmail', label: 'From Email', type: 'text' },
    { key: 'fromName', label: 'From Name', type: 'text' },
    { key: 'secure', label: 'Secure (SSL/TLS)', type: 'text' },
  ],
  rahmat: [
    { key: 'applicationId', label: 'Application ID', type: 'text', required: true },
    { key: 'secret', label: 'Secret Key', type: 'password', required: true },
    { key: 'storeId', label: 'Store ID', type: 'text', required: true },
    { key: 'sandbox', label: 'Sandbox Mode', type: 'toggle' },
    { key: 'backendBaseUrl', label: 'Backend Base URL', type: 'text', colSpan: 'full' },
    { key: 'ofdTin', label: 'ИНН/ПИНФЛ для чека', type: 'text' },
    { key: 'ofdMxik', label: 'ИКПУ (MXIK)', type: 'text' },
    { key: 'ofdPackageCode', label: 'Код упаковки', type: 'text' },
  ],
  // Отдельный мерчант для оплаты каналов; не заполнен — каналы идут через основной Rahmat.
  rahmat_channels: [
    { key: 'applicationId', label: 'Application ID', type: 'text', required: true },
    { key: 'secret', label: 'Secret Key', type: 'password', required: true },
    { key: 'storeId', label: 'Store ID', type: 'text', required: true },
    { key: 'sandbox', label: 'Sandbox Mode', type: 'toggle' },
    { key: 'backendBaseUrl', label: 'Backend Base URL', type: 'text', colSpan: 'full' },
    { key: 'ofdTin', label: 'ИНН/ПИНФЛ для чека', type: 'text' },
    { key: 'ofdMxik', label: 'ИКПУ (MXIK)', type: 'text' },
    { key: 'ofdPackageCode', label: 'Код упаковки', type: 'text' },
  ],
  cloudpayments: [
    {
      section: 'Основное',
      columns: 2,
      fields: [
        { key: 'publicId', label: 'Public ID', type: 'text', required: true, colSpan: 'half' },
        { key: 'apiSecret', label: 'API Secret', type: 'password', required: true, colSpan: 'half' },
        {
          key: 'region',
          label: 'Регион',
          type: 'select',
          colSpan: 'half',
          options: [
            { value: 'ru', label: 'Россия (ru)' },
            { value: 'eu', label: 'Европа (eu)' },
            { value: 'kz', label: 'Казахстан (kz)' },
          ],
        },
        { key: 'backendBaseUrl', label: 'Backend Base URL', type: 'text', required: true, colSpan: 'half' },
        { key: 'testMode', label: 'Тестовый режим', type: 'toggle', colSpan: 'full' },
      ],
    },
    {
      section: '54-ФЗ / Чеки',
      columns: 2,
      fields: [
        { key: 'receiptEnabled', label: 'Фискальные чеки', type: 'toggle', colSpan: 'full' },
        {
          // Глобальный провайдер фискализации. Хранится в SystemSetting'ах
          // через PUT /admin/fiscal/config, НЕ в ApiKey CloudPayments.
          // handleSave разделяет payload на ApiKey-поля и fiscal-поля.
          key: 'fiscalProvider',
          label: 'Провайдер чеков',
          type: 'select',
          colSpan: 'full',
          options: [
            { value: 'CLOUDPAYMENTS', label: 'CloudPayments (чек печатает CP)' },
            { value: 'BUSINESS_RU', label: 'Бизнес.ру (отдельная касса)' },
          ],
        },
        {
          // Префикс c_num — идентификатор чека в Бизнес.ру. Используется чтобы
          // отличать наши чеки от чеков других интеграций на одном аккаунте.
          // Меняется только до первой фискализации.
          key: 'fiscalCnumPrefix',
          label: 'Префикс c_num (идентификатор чека)',
          type: 'text',
          colSpan: 'full',
          placeholder: 'club / mc_platform / shop1',
          dependsOn: { key: 'fiscalProvider', value: 'BUSINESS_RU' },
        },
        {
          key: 'taxationSystem',
          label: 'Система налогообложения',
          type: 'select',
          colSpan: 'full',
          options: [
            { value: '0', label: '0 — ОСН' },
            { value: '1', label: '1 — УСН доход' },
            { value: '2', label: '2 — УСН доход-расход' },
            { value: '3', label: '3 — ЕНВД' },
            { value: '4', label: '4 — ЕСХН' },
            { value: '5', label: '5 — Патент' },
          ],
          dependsOn: { key: 'fiscalProvider', value: 'CLOUDPAYMENTS' },
        },
        {
          // Ставки НДС CloudKassir. С 01.01.2026 ставка 20% заменена на 22%
          // (расчётная 20/120 → 22/122). «Не облагается» (sentinel 'none')
          // сохраняется пустым значением → backend интерпретирует как null.
          key: 'vatRate',
          label: 'Ставка НДС',
          type: 'select',
          colSpan: 'half',
          options: [
            { value: 'none', label: 'Не облагается НДС' },
            { value: '0', label: 'НДС 0%' },
            { value: '5', label: 'НДС 5%' },
            { value: '7', label: 'НДС 7%' },
            { value: '10', label: 'НДС 10%' },
            { value: '20', label: 'НДС 20% (до 31.12.2025)' },
            { value: '22', label: 'НДС 22% (с 01.01.2026)' },
            { value: '105', label: 'Расчётный 5/105' },
            { value: '107', label: 'Расчётный 7/107' },
            { value: '110', label: 'Расчётный 10/110' },
            { value: '120', label: 'Расчётный 20/120 (до 31.12.2025)' },
            { value: '122', label: 'Расчётный 22/122 (с 01.01.2026)' },
          ],
          dependsOn: { key: 'fiscalProvider', value: 'CLOUDPAYMENTS' },
        },
        {
          key: 'merchantInn',
          label: 'ИНН юрлица',
          type: 'text',
          colSpan: 'half',
          dependsOn: { key: 'fiscalProvider', value: 'CLOUDPAYMENTS' },
        },
        {
          key: 'merchantFullName',
          label: 'Полное название юрлица',
          type: 'text',
          colSpan: 'full',
          dependsOn: { key: 'fiscalProvider', value: 'CLOUDPAYMENTS' },
        },
        // === Бизнес.ру (отдельная касса) — хранятся в SystemSetting, не в ApiKey ===
        {
          key: 'businessRuAccountUrl',
          label: 'URL аккаунта Бизнес.ру',
          type: 'text',
          colSpan: 'full',
          placeholder: 'https://check-dev.business.ru',
          dependsOn: { key: 'fiscalProvider', value: 'BUSINESS_RU' },
        },
        {
          key: 'businessRuAppId',
          label: 'App ID',
          type: 'text',
          colSpan: 'half',
          dependsOn: { key: 'fiscalProvider', value: 'BUSINESS_RU' },
        },
        {
          key: 'businessRuSecret',
          label: 'Secret Key',
          type: 'password',
          colSpan: 'half',
          dependsOn: { key: 'fiscalProvider', value: 'BUSINESS_RU' },
        },
        {
          key: 'businessRuCashier',
          label: 'ФИО кассира',
          type: 'text',
          colSpan: 'full',
          dependsOn: { key: 'fiscalProvider', value: 'BUSINESS_RU' },
        },
        {
          key: 'businessRuTaxationSystem',
          label: 'Система налогообложения (Бизнес.ру)',
          type: 'select',
          colSpan: 'full',
          options: [
            { value: '0', label: '0 — ОСН' },
            { value: '1', label: '1 — УСН доход' },
            { value: '2', label: '2 — УСН доход-расход' },
            { value: '3', label: '3 — ЕНВД' },
            { value: '4', label: '4 — ЕСХН' },
            { value: '5', label: '5 — Патент' },
          ],
          dependsOn: { key: 'fiscalProvider', value: 'BUSINESS_RU' },
        },
        {
          key: 'businessRuVatRate',
          label: 'Ставка НДС (Бизнес.ру)',
          type: 'select',
          colSpan: 'half',
          options: [
            { value: 'none', label: 'Не облагается НДС' },
            { value: '0', label: 'НДС 0%' },
            { value: '5', label: 'НДС 5%' },
            { value: '7', label: 'НДС 7%' },
            { value: '10', label: 'НДС 10%' },
            { value: '20', label: 'НДС 20% (до 31.12.2025)' },
            { value: '22', label: 'НДС 22% (с 01.01.2026)' },
            { value: '105', label: 'Расчётный 5/105' },
            { value: '107', label: 'Расчётный 7/107' },
            { value: '110', label: 'Расчётный 10/110' },
            { value: '120', label: 'Расчётный 20/120 (до 31.12.2025)' },
            { value: '122', label: 'Расчётный 22/122 (с 01.01.2026)' },
          ],
          dependsOn: { key: 'fiscalProvider', value: 'BUSINESS_RU' },
        },
        {
          // Ручной override callback URL. Пусто → backend сам соберёт
          // `<Backend Base URL>/api/fiscal/business-ru/callback` (см. блок-подсказку
          // BusinessRuCallbackInfo ниже). Задавать только если callback нужно
          // направить на другой хост (прокси/туннель).
          key: 'businessRuCallbackUrl',
          label: 'Callback URL (override, необязательно)',
          type: 'text',
          colSpan: 'full',
          placeholder: 'Пусто = авто из Backend Base URL',
          dependsOn: { key: 'fiscalProvider', value: 'BUSINESS_RU' },
        },
      ],
    },
    {
      section: 'Методы оплаты',
      columns: 2,
      fields: [
        { key: 'enableWidget', label: 'Виджет (Visa / MC / MIR)', type: 'toggle', colSpan: 'half' },
        { key: 'enableSbp', label: 'СБП', type: 'toggle', colSpan: 'half' },
        { key: 'enableTpay', label: 'T-Pay', type: 'toggle', colSpan: 'half' },
        { key: 'enableSberpay', label: 'SberPay', type: 'toggle', colSpan: 'half' },
        { key: 'enableApplePay', label: 'Apple Pay', type: 'toggle', colSpan: 'half' },
        { key: 'enableDolyame', label: 'Долями', type: 'toggle', colSpan: 'half' },
      ],
    },
    {
      section: 'Apple Pay',
      columns: 2,
      fields: [
        { key: 'appleMerchantId', label: 'Merchant ID', type: 'text', colSpan: 'half' },
        {
          key: 'appleCountryCode',
          label: 'Код страны',
          type: 'select',
          colSpan: 'half',
          options: [
            { value: 'RU', label: 'RU — Россия' },
            { value: 'KZ', label: 'KZ — Казахстан' },
            { value: 'UZ', label: 'UZ — Узбекистан' },
            { value: 'US', label: 'US — США' },
            { value: 'DE', label: 'DE — Германия' },
          ],
        },
        { key: 'appleDisplayName', label: 'Название магазина (DisplayName)', type: 'text', colSpan: 'half' },
        { key: 'appleMerchantCertPath', label: 'Путь к .pem сертификату', type: 'text', colSpan: 'half' },
      ],
    },
    {
      section: 'Долями',
      columns: 2,
      fields: [
        { key: 'dolyameMinAmount', label: 'Мин. сумма заказа', type: 'text', placeholder: '1000', colSpan: 'half' },
        { key: 'dolyameMaxAmount', label: 'Макс. сумма заказа', type: 'text', placeholder: '150000', colSpan: 'half' },
      ],
    },
  ] satisfies SectionDef[],
  google_oauth: [
    { key: 'clientId', label: 'Client ID', type: 'text', required: true },
    { key: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
    { key: 'redirectUri', label: 'Redirect URI', type: 'text' },
    { key: 'apiKey', label: 'Google API Key (для Drive)', type: 'password' },
  ],
};

function getRequiredFields(config: ServiceConfig | undefined): string[] {
  if (!config) {
    return [];
  }
  if (isSectioned(config)) {
    return config.flatMap((s) => s.fields.filter((f) => f.required).map((f) => f.key));
  }
  return config.filter((f) => f.required).map((f) => f.key);
}

interface FieldRowProps {
  field: FieldDef;
  value: string;
  onChange: (val: string) => void;
  disabled: boolean;
  error?: boolean;
}

function FieldRow({ field, value, onChange, disabled, error }: FieldRowProps) {
  const labelEl = (
    <label className={`text-sm font-medium leading-snug ${error ? 'text-destructive' : 'text-foreground/80'}`}>
      {field.label}
      {field.required && <span className="text-destructive ml-0.5">*</span>}
    </label>
  );

  if (field.type === 'toggle') {
    return (
      <div className="flex items-center justify-between gap-4 py-1.5 min-h-[36px]">
        {labelEl}
        <Switch
          checked={value === 'true'}
          onCheckedChange={(v) => onChange(v ? 'true' : 'false')}
          disabled={disabled}
        />
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className="space-y-1.5">
        {labelEl}
        <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger className={`bg-muted/30 ${error ? 'border-destructive' : ''}`}>
            <SelectValue placeholder={field.placeholder ?? `Выберите ${field.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {labelEl}
      <div className="flex gap-2">
        <Input
          type={field.type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? (field.type === 'password' ? '••••••••' : `Введите ${field.label.toLowerCase()}`)}
          className={`bg-muted/30 focus-visible:ring-primary ${error ? 'border-destructive' : ''}`}
          disabled={disabled}
          autoComplete={field.type === 'password' ? 'new-password' : 'off'}
        />
        {field.type === 'password' && value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive shrink-0"
            onClick={() => onChange('')}
            title="Очистить значение"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

const CP_WEBHOOK_ROUTES = [
  { label: 'Check',     path: '/cloudpayments/webhook/check' },
  { label: 'Pay',       path: '/cloudpayments/webhook/pay' },
  { label: 'Fail',      path: '/cloudpayments/webhook/fail' },
  { label: 'Confirm',   path: '/cloudpayments/webhook/confirm' },
  { label: 'Refund',    path: '/cloudpayments/webhook/refund' },
  { label: 'Cancel',    path: '/cloudpayments/webhook/cancel' },
  { label: 'Recurrent', path: '/cloudpayments/webhook/recurrent' },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      title="Скопировать"
    >
      {copied
        ? <Check className="h-3.5 w-3.5 text-green-500" />
        : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function CloudPaymentsWebhookInfo({ backendUrl }: { backendUrl: string }) {
  const envBase = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/api\/?$/, '').replace(/\/$/, '') ?? '';
  const base = backendUrl.replace(/\/$/, '') || envBase || '{backend_base_url}';
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Webhook URL для панели CloudPayments
      </p>
      <div className="space-y-1.5">
        {CP_WEBHOOK_ROUTES.map((r) => {
          const full = `${base}${r.path}`;
          return (
            <div key={r.path} className="flex items-center gap-2 group">
              <span className="text-xs text-muted-foreground w-16 shrink-0">{r.label}</span>
              <code className="flex-1 text-xs font-mono bg-muted/40 rounded px-2 py-1 truncate text-foreground/80">
                {full}
              </code>
              <CopyButton text={full} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Callback URL Бизнес.ру — рассчитывается автоматически.
 * Приоритет: backendUrl (live-значение из формы) → серверный effectiveCallbackUrl → env VITE_API_URL.
 * Этот URL нужно вбить в ЛК Бизнес.ру → Интеграции → Открытое API → callback.
 * Без него Бизнес.ру не пушит результат фискализации, работаем через polling.
 */
function BusinessRuCallbackInfo({ backendUrl, effectiveUrl }: { backendUrl: string; effectiveUrl: string }) {
  const envBase = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/api\/?$/, '').replace(/\/$/, '') ?? '';
  const base = backendUrl.replace(/\/$/, '') || envBase;
  const built = base ? `${base}/api/fiscal/business-ru/callback` : '';
  const url = built || effectiveUrl;
  const display = url || '{Backend Base URL}/api/fiscal/business-ru/callback';
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Callback URL для ЛК Бизнес.ру
      </p>
      <div className="flex items-center gap-2 group">
        <code className="flex-1 text-xs font-mono bg-muted/40 rounded px-2 py-1 truncate text-foreground/80">
          {display}
        </code>
        {url && <CopyButton text={url} />}
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">
        Вбейте в ЛК Бизнес.ру → Интеграции → Открытое API → callback. URL
        собирается автоматически из Backend Base URL раздела «Основное».
        Без callback фискализация работает через polling (медленнее).
      </p>
    </div>
  );
}

interface SectionGridProps {
  section: SectionDef;
  values: Record<string, string>;
  errors: Record<string, boolean>;
  onChange: (key: string, val: string) => void;
  disabled: boolean;
}

function SectionGrid({ section, values, errors, onChange, disabled }: SectionGridProps) {
  const use2col = section.columns === 2;

  // Фильтруем поля по dependsOn: если зависимое поле не имеет ожидаемого значения,
  // поле скрываем. Это позволяет условно показывать блоки внутри секции
  // (например, поля Бизнес.ру vs CloudPayments в секции «54-ФЗ / Чеки»).
  const visibleFields = section.fields.filter((field) => {
    if (!field.dependsOn) {return true;}
    return values[field.dependsOn.key] === field.dependsOn.value;
  });

  if (visibleFields.length === 0) {return null;}

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {section.section}
        </span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {use2col ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {visibleFields.map((field) => (
            <div key={field.key} className={field.colSpan === 'full' || field.type === 'toggle' ? 'col-span-2' : 'col-span-1'}>
              <FieldRow
                field={field}
                value={values[field.key] || ''}
                onChange={(val) => onChange(field.key, val)}
                disabled={disabled}
                error={errors[field.key]}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {visibleFields.map((field) => (
            <FieldRow
              key={field.key}
              field={field}
              value={values[field.key] || ''}
              onChange={(val) => onChange(field.key, val)}
              disabled={disabled}
              error={errors[field.key]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ApiKeysList() {
  useScrollRestoration();
  const [editingService, setEditingService] = useState<IntegrationService | null>(null);
  const [editValue, setEditValue] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [search, setSearch] = usePersistedState('api-keys:search', '');
  const [sort, setSort] = usePersistedState<SortState | null>('api-keys:sort', null);
  const invalidate = useInvalidate();

  const { result, query } = useList<IntegrationService>({
    resource: 'integrations',
    pagination: { mode: 'off' },
  });

  const allServices = result.data;
  const isLoading = query.isLoading;

  // Client-side search + sort
  const services = useMemo(() => {
    let items = allServices;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      items = items.filter(
        (s) =>
          s.label.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q),
      );
    }
    if (sort) {
      const { field, order } = sort;
      items = [...items].sort((a, b) => {
        const aVal: unknown = a[field as keyof IntegrationService];
        const bVal: unknown = b[field as keyof IntegrationService];
        let cmp = 0;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          cmp = aVal.localeCompare(bVal);
        } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
          cmp = (aVal ? 1 : 0) - (bVal ? 1 : 0);
        }
        return order === 'asc' ? cmp : -cmp;
      });
    }
    return items;
  }, [allServices, search, sort]);

  const { mutate: deleteMutation } = useDelete();

  const { mutate: updateMutation, mutation } = useUpdate<IntegrationService>();
  const isUpdating = mutation.isPending;

  const smtpTestMutation = useCustomMutation();
  const isTestingSMTP = smtpTestMutation.mutation.isPending;

  // Fiscal config — глобальный (один на проект). Грузим явным fetch в state, а
  // НЕ через useCustom: возврат useCustom для этого эндпоинта непредсказуемо
  // вкладывался (.data vs .data.data) и форма залипала на дефолте CLOUDPAYMENTS.
  // Прямой fetch + глобальный fetch-интерцептор (JWT) детерминирован.
  const [fiscalConfig, setFiscalConfig] = useState<FiscalConfigResponse | undefined>(undefined);
  const refetchFiscalConfig = useMemo(
    () => async () => {
      try {
        const res = await fetch(`${resolveApiUrl()}/admin/fiscal/config`);
        if (!res.ok) {
          return;
        }
        const json: ApiResponse<FiscalConfigResponse> = await res.json();
        if (json.success && json.data) {
          setFiscalConfig(json.data);
        }
      } catch {
        // сеть/парс — оставляем прежнее значение, форма работает на дефолтах
      }
    },
    [],
  );
  useEffect(() => {
    void refetchFiscalConfig();
  }, [refetchFiscalConfig]);

  const fiscalConfigMutation = useCustomMutation();
  const fiscalHealthcheckMutation = useCustomMutation<FiscalHealthcheckResponse>();
  const isHealthchecking = fiscalHealthcheckMutation.mutation.isPending;

  const handleSmtpTest = () => {
    smtpTestMutation.mutate(
      { url: '/integrations/smtp/test', method: 'post', values: {} },
      {
        onSuccess: () => notify.success('Тестовое письмо отправлено'),
        onError: (error) => notify.error(translateErrorMessage(error.message)),
      },
    );
  };

  const handleFiscalHealthcheck = () => {
    fiscalHealthcheckMutation.mutate(
      { url: '/admin/fiscal/business-ru/healthcheck', method: 'post', values: {} },
      {
        onSuccess: (res) => {
          const result: FiscalHealthcheckResponse | undefined = res?.data;
          if (result?.ok) {
            notify.success('Связь с Бизнес.ру установлена');
          } else {
            notify.error(result?.error ?? 'Бизнес.ру вернул ошибку');
          }
        },
        onError: (error) => notify.error(translateErrorMessage(error.message)),
      },
    );
  };

  // Доливает fiscal-config (provider/cnumPrefix/businessRu.*) в editValue для
  // формы cloudpayments. Вынесено из handleEdit, чтобы тот же мерж можно было
  // применить из useEffect — fiscalConfig грузится асинхронно и может прийти
  // ПОСЛЕ открытия модалки (иначе провайдер залипает в дефолте CLOUDPAYMENTS).
  const applyFiscalConfig = (
    target: Record<string, string>,
    cfg: FiscalConfigResponse | undefined,
  ): Record<string, string> => {
    const next = { ...target };
    next.fiscalProvider = cfg?.provider ?? 'CLOUDPAYMENTS';
    next.fiscalCnumPrefix = cfg?.cnumPrefix ?? 'club';
    if (cfg?.businessRu) {
      const br = cfg.businessRu;
      next.businessRuAccountUrl = br.accountUrl ?? '';
      next.businessRuAppId = br.appId ?? '';
      // secret приходит маскированным '***' — оставляем для отображения
      next.businessRuSecret = br.secret ?? '';
      next.businessRuCashier = br.cashier ?? '';
      next.businessRuTaxationSystem = br.taxationSystem ?? '0';
      next.businessRuCallbackUrl = br.callbackUrl ?? '';
      const brVat = (br.vatRate ?? '').trim();
      next.businessRuVatRate = !brVat || brVat === '-1' ? 'none' : brVat;
    }
    return next;
  };

  const handleEdit = (service: IntegrationService) => {
    setEditingService(service);
    // Нормализация vatRate под Select: пустое значение, legacy-код '-1'
    // или отсутствие → sentinel 'none' («Не облагается НДС»).
    let normalized: Record<string, string> = { ...service.value };
    if (service.key === 'cloudpayments') {
      const vat = normalized.vatRate?.trim();
      if (!vat || vat === '-1') {
        normalized.vatRate = 'none';
      }
      normalized = applyFiscalConfig(normalized, fiscalConfig);
    }
    setEditValue(normalized);
    setFieldErrors({});
  };

  // Если модалка cloudpayments открылась раньше, чем загрузился fiscalConfig
  // (useCustom асинхронен), handleEdit выставил дефолт CLOUDPAYMENTS. Этот
  // эффект доливает реальный конфиг, как только он приходит, не затирая
  // прочие поля формы. Реагирует на смену провайдера/префикса в конфиге.
  useEffect(() => {
    if (editingService?.key !== 'cloudpayments' || !fiscalConfig) {
      return;
    }
    setEditValue((prev) => {
      // Уже синхронизировано (тот же провайдер) — не триггерим лишний ре-рендер.
      if (prev.fiscalProvider === fiscalConfig.provider) {
        return prev;
      }
      return applyFiscalConfig(prev, fiscalConfig);
    });
    // applyFiscalConfig стабилен по сигнатуре; deps — id формы и сам конфиг.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingService?.key, fiscalConfig?.provider, fiscalConfig?.cnumPrefix]);

  const handleFieldChange = (key: string, val: string) => {
    setEditValue((prev) => ({ ...prev, [key]: val }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSave = () => {
    if (!editingService) return;

    const config = SERVICE_FIELDS[editingService.key];
    let required = getRequiredFields(config);

    // При provider=BUSINESS_RU касса Бизнес.ру — отдельная, CloudPayments-креды
    // (publicId/apiSecret) не нужны. Не требуем их, иначе форма блокирует
    // сохранение настроек Бизнес.ру из-за пустых CP-полей.
    const isBusinessRuProvider =
      editingService.key === 'cloudpayments' && editValue.fiscalProvider === 'BUSINESS_RU';
    if (isBusinessRuProvider) {
      required = required.filter((key) => key !== 'publicId' && key !== 'apiSecret');
    }

    const errors: Record<string, boolean> = {};
    for (const key of required) {
      const val = editValue[key];
      // Маска из звёздочек в поле означает «значение уже сохранено на сервере»
      // (секретные поля backend наружу не отдаёт). Считать её незаполненностью
      // нельзя: иначе у настроенного сервиса невозможно поменять несекретное
      // поле, не перевводя ключ вручную. Пустым считаем только то, чего нет и
      // на сервере — там маски в value тоже не будет.
      const savedOnServer = Boolean(editingService.value?.[key]);
      if (!val || (/^\*+$/.test(val) && !savedOnServer)) {
        errors[key] = true;
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      notify.error('Заполните обязательные поля');
      return;
    }

    const cleanedValues: Record<string, string> = {};
    for (const [key, value] of Object.entries(editValue)) {
      if (typeof value === 'string' && !/^\*+$/.test(value)) {
        cleanedValues[key] = value;
      }
    }

    // vatRate: sentinel 'none' («Не облагается НДС») → пустая строка,
    // backend-парсер превращает её в null. Radix Select запрещает value=''.
    if (cleanedValues.vatRate === 'none') {
      cleanedValues.vatRate = '';
    }

    // Для cloudpayments отделяем fiscal-поля: они сохраняются отдельным
    // запросом в SystemSetting (PUT /admin/fiscal/config), а НЕ в ApiKey.
    let fiscalPayload: {
      provider?: 'CLOUDPAYMENTS' | 'BUSINESS_RU';
      cnumPrefix?: string;
      businessRu?: {
        accountUrl?: string;
        appId?: string;
        secret?: string;
        cashier?: string;
        taxationSystem?: string;
        vatRate?: string;
        callbackUrl?: string;
      };
    } | null = null;

    if (editingService.key === 'cloudpayments') {
      const fp = cleanedValues.fiscalProvider;
      const cnumPrefix = cleanedValues.fiscalCnumPrefix;
      const brAccountUrl = cleanedValues.businessRuAccountUrl;
      const brAppId = cleanedValues.businessRuAppId;
      const brSecret = cleanedValues.businessRuSecret;
      const brCashier = cleanedValues.businessRuCashier;
      const brTaxation = cleanedValues.businessRuTaxationSystem;
      const brVatRaw = cleanedValues.businessRuVatRate;
      const brCallbackUrl = cleanedValues.businessRuCallbackUrl;

      fiscalPayload = {};
      if (fp === 'CLOUDPAYMENTS' || fp === 'BUSINESS_RU') {
        fiscalPayload.provider = fp;
      }
      if (cnumPrefix !== undefined && cnumPrefix.trim().length > 0) {
        // Клиентская валидация — повторяет бэкенд: [a-zA-Z0-9_-], 1-32 символа.
        if (!/^[a-zA-Z0-9_-]{1,32}$/.test(cnumPrefix.trim())) {
          setFieldErrors((prev) => ({ ...prev, fiscalCnumPrefix: true }));
          notify.error('Префикс c_num: только латиница/цифры/_/-, 1-32 символа');
          return;
        }
        fiscalPayload.cnumPrefix = cnumPrefix.trim();
      }
      const businessRu: NonNullable<typeof fiscalPayload.businessRu> = {};
      if (brAccountUrl !== undefined) {businessRu.accountUrl = brAccountUrl;}
      if (brAppId !== undefined) {businessRu.appId = brAppId;}
      if (brSecret !== undefined) {businessRu.secret = brSecret;}
      if (brCashier !== undefined) {businessRu.cashier = brCashier;}
      if (brTaxation !== undefined) {businessRu.taxationSystem = brTaxation;}
      if (brVatRaw !== undefined) {
        businessRu.vatRate = brVatRaw === 'none' ? '' : brVatRaw;
      }
      // callbackUrl: пустая строка тоже отправляется — так бэкенд обнулит ручной
      // override и снова начнёт собирать callback автоматически из Backend Base URL.
      if (brCallbackUrl !== undefined) {businessRu.callbackUrl = brCallbackUrl.trim();}
      if (Object.keys(businessRu).length > 0) {
        fiscalPayload.businessRu = businessRu;
      }

      // Удаляем fiscal-ключи из ApiKey-payload — они хранятся в SystemSetting.
      delete cleanedValues.fiscalProvider;
      delete cleanedValues.fiscalCnumPrefix;
      delete cleanedValues.businessRuAccountUrl;
      delete cleanedValues.businessRuAppId;
      delete cleanedValues.businessRuSecret;
      delete cleanedValues.businessRuCashier;
      delete cleanedValues.businessRuTaxationSystem;
      delete cleanedValues.businessRuVatRate;
      delete cleanedValues.businessRuCallbackUrl;
    }

    const hasFiscalPayload = Boolean(
      fiscalPayload &&
        (fiscalPayload.provider || fiscalPayload.cnumPrefix || fiscalPayload.businessRu),
    );

    // Сохранение fiscal-конфига (PUT /admin/fiscal/config) — НЕЗАВИСИМО от ApiKey.
    // Раньше этот запрос жил во вложенном onSuccess updateMutation: если ApiKey
    // CloudPayments не сохранялся (пустые/невалидные CP-креды при provider=
    // BUSINESS_RU), fiscal-payload никогда не уходил → настройки Бизнес.ру
    // молча терялись. Теперь fiscal сохраняется отдельно.
    const saveFiscal = (closeAfter: boolean) => {
      if (!fiscalPayload || !hasFiscalPayload) {
        if (closeAfter) { setEditingService(null); }
        void invalidate({ resource: 'integrations', invalidates: ['list'] });
        return;
      }
      fiscalConfigMutation.mutate(
        { url: '/admin/fiscal/config', method: 'put', values: fiscalPayload },
        {
          onSuccess: () => {
            notify.success('Настройки фискализации сохранены');
            if (closeAfter) { setEditingService(null); }
            void refetchFiscalConfig();
            void invalidate({ resource: 'integrations', invalidates: ['list'] });
          },
          onError: (error) => notify.error(translateErrorMessage(error.message)),
        },
      );
    };

    // ApiKey CloudPayments обновляем только если есть что сохранять. При
    // provider=BUSINESS_RU с пустыми CP-полями cleanedValues пуст — апдейт
    // ApiKey пропускаем и сохраняем только fiscal-конфиг.
    const hasApiKeyValues = Object.keys(cleanedValues).length > 0;
    if (!hasApiKeyValues) {
      saveFiscal(true);
      return;
    }

    updateMutation(
      { resource: 'integrations', id: editingService.id, values: cleanedValues },
      {
        onSuccess: () => {
          saveFiscal(true);
        },
        onError: (error) => {
          notify.error(translateErrorMessage(error.message));
          // ApiKey не сохранился, но fiscal-настройки (отдельный ресурс)
          // всё равно сохраняем — они не зависят от CP-кредов.
          if (hasFiscalPayload) {
            saveFiscal(false);
          }
        },
      },
    );
  };

  const columns: Column<IntegrationService>[] = [
    {
      key: 'label',
      header: 'Интеграция',
      sortable: true,
      render: (_, row) => (
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-24 items-center justify-center p-2">
            <img src={row.logo} alt={row.label} className="max-h-full max-w-full object-contain dark:invert" />
          </div>
          <div>
            <div className="font-medium text-base">{row.label}</div>
            <div className="text-xs text-muted-foreground uppercase opacity-60">{row.category}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'isConfigured',
      header: 'Статус',
      sortable: true,
      render: (_, row) =>
        row.isConfigured ? (
          <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20 px-3 py-1 border-green-500/20">
            Настроено
          </Badge>
        ) : (
          <Badge variant="secondary" className="px-3 py-1 opacity-70">
            Не настроено
          </Badge>
        ),
    },
    {
      key: 'key',
      header: 'Параметры',
      render: (_, row) => {
        // Для сервиса без описания полей показываем прочерк, а не роняем
        // таблицу: бэкенд может вернуть интеграцию, которой здесь ещё нет.
        const config = SERVICE_FIELDS[row.key];
        if (!config) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        const count = isSectioned(config)
          ? config.reduce((acc, s) => acc + s.fields.length, 0)
          : config.length;
        return <span className="text-muted-foreground text-sm">{count} полей</span>;
      },
    },
  ];

  const handleDelete = (items: IntegrationService[]) => {
    if (!confirm(`Удалить ${items.length > 1 ? `${items.length} интеграции` : `«${items[0].label}»`}?`)) {
      return;
    }
    for (const item of items) {
      deleteMutation(
        { resource: 'integrations', id: item.id },
        {
          onSuccess: () => {
            void invalidate({ resource: 'integrations', invalidates: ['list'] });
          },
          onError: (error) => notify.error(translateErrorMessage(error.message)),
        },
      );
    }
  };

  const bulkActions: BulkAction<IntegrationService>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => { if (items.length === 1) handleEdit(items[0]); },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Редактировать',
      icon: <Edit className="h-4 w-4" />,
      onClick: (items) => { if (items.length === 1) handleEdit(items[0]); },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: handleDelete,
      showOnlyWhenSelected: true,
    },
  ];

  const currentServiceConfig = editingService ? SERVICE_FIELDS[editingService.key] : null;

  return (
    <div className="space-y-6">
      <FormHeader
        title="Интеграции"
        description="Централизованное управление API ключами и настройками внешних сервисов"
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Поиск по названию или категории..."
      />

      <div className="rounded-xl border bg-card shadow-sm">
        <DataTable
          columns={columns}
          data={services}
          total={services.length}
          page={1}
          pageSize={50}
          isLoading={isLoading}
          onPageChange={() => { /* noop */ }}
          onRowClick={(row) => handleEdit(row)}
          emptyMessage="Нет доступных интеграций"
          selectable
          rowKey="id"
          sort={sort ?? undefined}
          onSortChange={(s) => setSort(s ?? null)}
          bulkActions={bulkActions}
        />
      </div>

      <Dialog open={!!editingService} onOpenChange={(open) => !open && setEditingService(null)}>
        <DialogContent className="max-w-2xl flex flex-col max-h-[90vh]">
          <DialogHeader className="shrink-0">
            <div className="flex items-center gap-4 mb-1">
              <div className="flex h-10 w-20 items-center justify-center p-2">
                <img
                  src={editingService?.logo}
                  alt={editingService?.label}
                  className="max-h-full max-w-full object-contain dark:invert"
                />
              </div>
              <div>
                <DialogTitle className="text-xl">
                  Настройка {editingService?.label}
                </DialogTitle>
                <DialogDescription>
                  Введите учетные данные для {editingService?.label}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <form
            onSubmit={(e) => { e.preventDefault(); handleSave(); }}
            className="flex flex-col flex-1 min-h-0"
          >
            <div className="overflow-y-auto flex-1 space-y-5 py-4 pr-2">
              {currentServiceConfig && isSectioned(currentServiceConfig)
                ? currentServiceConfig.map((section, idx) => (
                  <div key={section.section} className="space-y-3">
                    <SectionGrid
                      section={section}
                      values={editValue}
                      errors={fieldErrors}
                      onChange={handleFieldChange}
                      disabled={isUpdating}
                    />
                    {editingService?.key === 'cloudpayments' && idx === 0 && (
                      <CloudPaymentsWebhookInfo backendUrl={editValue['backendBaseUrl'] || ''} />
                    )}
                    {editingService?.key === 'cloudpayments' &&
                      idx === 1 &&
                      editValue.fiscalProvider === 'BUSINESS_RU' && (
                        <BusinessRuCallbackInfo
                          backendUrl={editValue['backendBaseUrl'] || ''}
                          effectiveUrl={fiscalConfig?.businessRu?.effectiveCallbackUrl ?? ''}
                        />
                      )}
                  </div>
                ))
                : (currentServiceConfig ?? []).map((field) => (
                  <FieldRow
                    key={field.key}
                    field={field}
                    value={editValue[field.key] || ''}
                    onChange={(val) => handleFieldChange(field.key, val)}
                    disabled={isUpdating}
                    error={fieldErrors[field.key]}
                  />
                ))}
            </div>

            <DialogFooter className="pt-4 border-t gap-2 shrink-0">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditingService(null)}
                disabled={isUpdating}
                className="px-6"
              >
                Отмена
              </Button>
              {editingService?.key === 'email_smtp' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSmtpTest}
                  disabled={isTestingSMTP || isUpdating}
                  className="px-6"
                >
                  {isTestingSMTP ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Тест
                </Button>
              )}
              {editingService?.key === 'cloudpayments' &&
                editValue.fiscalProvider === 'BUSINESS_RU' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleFiscalHealthcheck}
                    disabled={isHealthchecking || isUpdating}
                    className="px-6"
                  >
                    {isHealthchecking ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    Проверить Бизнес.ру
                  </Button>
                )}
              <Button type="submit" disabled={isUpdating} className="px-8 shadow-sm">
                {isUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Сохранить
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
